import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useRef } from "react";
import { defineMessages, useIntl } from "react-intl";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

import YouSureSVG from "@/assets/images/YouSure.svg";
import { cn } from "@/utils/core/generalUtils";

const customAlertMessages = defineMessages({
  cancelAction: {
    id: "components.customAlert.cancelAction",
    defaultMessage: "No, Continue Editing",
  },
  confirmAction: {
    id: "components.customAlert.confirmAction",
    defaultMessage: "Yes, Cancel",
  },
  description: {
    id: "components.customAlert.description",
    defaultMessage:
      "if you delete this already used name will be as it is. if you add new on same name it will be treated as new one.",
  },
  imageAlt: {
    id: "components.customAlert.imageAlt",
    defaultMessage: "Confirmation",
  },
  loading: {
    id: "components.customAlert.loading",
    defaultMessage: "Loading...",
  },
  title: {
    id: "components.customAlert.title",
    defaultMessage: " Are You Sure ?",
  },
});

interface AlertProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  btnName?: string;
  cancelName?: string;
  btnAction?: () => void;
  cancelAction?: () => void;
  title?: string;
  desc?: string;
  img?: string;
  imgSize?: number;
  customElement?: React.ReactNode;
  showImg?: boolean;
  className?: string;
  footerElementAttr?: {
    className?: string;
    order?: "first" | "last";
  };
  node?: {
    className?: string;
    contentClassName?: string;
    hideFooter?: boolean;
    node: React.ReactNode;
  };
  meta?: {
    hideTitle?: boolean;
    hideDesc?: boolean;
  };
  isLoading?: boolean;
}

const CustomAlert: React.FC<AlertProps> = (props: AlertProps) => {
  const intl = useIntl();
  const {
    isOpen,
    setIsOpen,
    title,
    btnName,
    cancelName,
    btnAction,
    cancelAction,
    desc,
    img,
    imgSize,
    customElement,
    showImg = true,
    footerElementAttr = {
      order: "first",
    },
    meta = {
      hideTitle: false,
      hideDesc: false,
    },
    node,
    isLoading = false,
  } = props;
  const buttonClickedRef = useRef(false);
  const sharedFooter = (
    <DialogFooter className='mt-6'>
      <div
        className={cn(
          "w-full flex flex-col gap-2 items-center",
          footerElementAttr?.className,
        )}
      >
        <DialogClose asChild disabled={isLoading}>
          <Button
            onClick={() => {
              buttonClickedRef.current = true;
              btnAction?.();
            }}
            disabled={isLoading}
            className={cn(
              "w-4/5",
              footerElementAttr?.order === "first" ? "order-1" : "order-last",
            )}
            message={intl.formatMessage(customAlertMessages.loading)}
          >
            {btnName?.length
              ? btnName
              : intl.formatMessage(customAlertMessages.confirmAction)}
          </Button>
        </DialogClose>
        <DialogClose asChild>
          <Button
            variant={"ghost"}
            className='order-1 w-4/5 border-0 ring-0 hover:border'
            onClick={() => {
              buttonClickedRef.current = true;
              cancelAction?.();
            }}
            disabled={isLoading}
          >
            {cancelName?.length
              ? cancelName
              : intl.formatMessage(customAlertMessages.cancelAction)}
          </Button>
        </DialogClose>
      </div>
    </DialogFooter>
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !buttonClickedRef.current) {
          cancelAction?.();
        }
        buttonClickedRef.current = false;
        setIsOpen(open);
      }}
    >
      <DialogContent
        className={cn(node?.className, "w-[450px] py-4")}
        contentClassName={node?.contentClassName}
      >
        <VisuallyHidden>
          <DialogTitle />
          <DialogDescription />
        </VisuallyHidden>
        {node ? (
          <>
            {node.node}
            {!node.hideFooter && sharedFooter}
          </>
        ) : (
          <>
            <DialogHeader>
              {showImg && (
                <img
                  src={img ?? YouSureSVG}
                  className={`${img ? `size-[${imgSize}px]` : "w-4/5 h-[11/12] "} self-center`}
                  alt={intl.formatMessage(customAlertMessages.imageAlt)}
                  loading='eager'
                />
              )}
            </DialogHeader>
            <div className='text-center'>
              {!meta.hideTitle && (
                <h2 className='pb-2 text-2xl font-medium'>
                  {title?.length
                    ? title
                    : intl.formatMessage(customAlertMessages.title)}
                </h2>
              )}
              {!meta.hideDesc && (
                <p className='text-base text-aldgray opacity-70'>
                  {desc?.length
                    ? desc
                    : intl.formatMessage(customAlertMessages.description)}
                </p>
              )}
              {customElement}
            </div>
            {sharedFooter}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CustomAlert;
